import { TFunction } from "i18next";
import {
  ServiceBriefingKind,
  ServiceBriefingStatus,
} from "../../types/serviceBriefing";

export const SERVICE_BRIEFING_KINDS: ServiceBriefingKind[] = [
  "HOUSE_OF_STORIES",
  "HOUSE_OF_AI",
  "HOUSE_OF_DIGITAL",
];

export const SERVICE_BRIEFING_STATUSES: ServiceBriefingStatus[] = [
  "DRAFT",
  "REQUESTED",
  "IN_PROGRESS",
  "DELIVERED",
  "CANCELLED",
];

export const kindLabel = (t: TFunction, kind: ServiceBriefingKind): string =>
  ({
    HOUSE_OF_STORIES: t(
      "serviceBriefings.kind.HOUSE_OF_STORIES",
      "House of Stories"
    ),
    HOUSE_OF_AI: t("serviceBriefings.kind.HOUSE_OF_AI", "House of AI"),
    HOUSE_OF_DIGITAL: t(
      "serviceBriefings.kind.HOUSE_OF_DIGITAL",
      "House of Digital"
    ),
  })[kind];

export const statusLabel = (
  t: TFunction,
  status: ServiceBriefingStatus
): string =>
  ({
    DRAFT: t("serviceBriefings.status.DRAFT", "Borrador"),
    REQUESTED: t("serviceBriefings.status.REQUESTED", "Solicitado"),
    IN_PROGRESS: t("serviceBriefings.status.IN_PROGRESS", "En curso"),
    DELIVERED: t("serviceBriefings.status.DELIVERED", "Entregado"),
    CANCELLED: t("serviceBriefings.status.CANCELLED", "Cancelado"),
  })[status];

export const STATUS_COLOR: Record<
  ServiceBriefingStatus,
  "default" | "info" | "warning" | "success" | "error"
> = {
  DRAFT: "default",
  REQUESTED: "info",
  IN_PROGRESS: "warning",
  DELIVERED: "success",
  CANCELLED: "error",
};

export const euro = new Intl.NumberFormat("es-ES", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** "2026-10-22" → "22/10/2026", sin pasar por Date para no moverse de día con la zona. */
export const formatDay = (day: string | null | undefined): string => {
  if (!day) return "—";
  const [y, m, d] = day.split("-");
  return `${d}/${m}/${y}`;
};

/** Hoy en Madrid como "YYYY-MM-DD", que es lo que espera un input type="date". */
export const todayInMadrid = (): string =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Madrid",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());

/**
 * Con la entrega pasada y el trabajo sin cerrar. Un entregado o cancelado no se marca
 * nunca, aunque la fecha sea antigua.
 */
export const isOverdue = (
  deliveryDate: string | null,
  status: ServiceBriefingStatus
): boolean =>
  Boolean(deliveryDate) &&
  status !== "DELIVERED" &&
  status !== "CANCELLED" &&
  (deliveryDate as string) < todayInMadrid();

export const formatBytes = (bytes: number | null): string => {
  if (bytes === null || bytes === undefined) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Misma fórmula que compone el backend; solo para enseñarlo antes de guardar. */
export const previewName = (
  kind: ServiceBriefingKind,
  month: number,
  year: number,
  brandOrProduct: string,
  campaignName: string
): string =>
  [
    String(month).padStart(2, "0"),
    String(year),
    kind,
    brandOrProduct.trim(),
    campaignName.trim(),
  ]
    .filter((part) => part)
    .join("_");
